import { useEffect, useState } from "react";
import { useBranding } from "../branding";
import type { AuthUser } from "../types";

interface Props {
  login: (email: string, password: string) => Promise<{ user: AuthUser } | { needsTwoFactor: true }>;
  verifyTwoFactor: (opts: { token?: string; backupCode?: string }) => Promise<AuthUser>;
  register: (
    email: string,
    name: string,
    password: string,
  ) => Promise<{ user: AuthUser; needsApproval: boolean }>;
}

type Tab = "login" | "register" | "forgot" | "twofactor";

const SSO_LABEL: Record<"google" | "microsoft" | "github", string> = {
  google: "Google",
  microsoft: "Microsoft",
  github: "GitHub",
};

export function AuthPage({ login, verifyTwoFactor, register }: Props) {
  const { appName, logoDataUrl } = useBranding();
  const [tab, setTab] = useState<Tab>("login");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingMsg, setPendingMsg] = useState<string | null>(null);
  const [expirou, setExpirou] = useState(false);
  const [ssoProviders, setSsoProviders] = useState<{ google: boolean; microsoft: boolean; github: boolean } | null>(null);
  const [showSaml, setShowSaml] = useState(false);
  const [samlOrgSlug, setSamlOrgSlug] = useState("");

  // veio de uma sessão que caiu por inatividade (marcado no useAuth)
  useEffect(() => {
    if (sessionStorage.getItem("ea_sessao_expirada") === "1") {
      sessionStorage.removeItem("ea_sessao_expirada");
      setExpirou(true);
    }
  }, []);

  // provedores de login social configurados (epico 7) - inert até configurados
  useEffect(() => {
    let alive = true;
    fetch("/api/health")
      .then((r) => r.json())
      .then((j: { ssoProviders?: { google: boolean; microsoft: boolean; github: boolean } }) => {
        if (alive && j.ssoProviders) setSsoProviders(j.ssoProviders);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  // erro vindo do callback de login social/SAML (redirect com ?ssoError=...)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ssoError = params.get("ssoError");
    if (ssoError) {
      setError(ssoError);
      params.delete("ssoError");
      const rest = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (rest ? `?${rest}` : ""));
    }
  }, []);

  const ssoConfigured = ssoProviders ? (Object.keys(ssoProviders) as Array<keyof typeof ssoProviders>).filter((p) => ssoProviders[p]) : [];

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    setPendingMsg(null);
    try {
      if (tab === "login") {
        const result = await login(email.trim(), password);
        if ("needsTwoFactor" in result) {
          setTab("twofactor");
          setPassword("");
        }
        // sucesso: o App re-renderiza sozinho ao detectar o usuário
      } else if (tab === "twofactor") {
        await verifyTwoFactor(
          useBackupCode ? { backupCode: twoFactorCode.trim() } : { token: twoFactorCode.trim() },
        );
        // sucesso: o App re-renderiza sozinho ao detectar o usuário
      } else if (tab === "forgot") {
        const r = await fetch("/api/auth/forgot-password", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ email: email.trim() }),
        });
        const j = (await r.json().catch(() => ({}))) as { mensagem?: string; error?: string };
        if (!r.ok) {
          setError(j.error ?? "Falha ao pedir o link.");
          return;
        }
        // a resposta é a mesma exista a conta ou não: a tela conta o que foi
        // feito, não o que existe do outro lado
        setPendingMsg(j.mensagem ?? "Pedido registrado.");
        setTab("login");
        setPassword("");
      } else {
        const { needsApproval } = await register(email.trim(), name.trim(), password);
        if (needsApproval) {
          setPendingMsg(
            "Conta criada! Ela precisa ser aprovada pelo administrador antes do primeiro acesso. Você será avisado quando for liberada.",
          );
          setTab("login");
          setPassword("");
        }
        // sysadmin (ADMIN_EMAILS): sucesso sem needsApproval já entra logado
        // (cookie setado no servidor), o App re-renderiza sozinho
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Falha ao autenticar.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-page auth-split">
      <aside className="auth-hero" aria-hidden>
        <div className="auth-hero-inner">
          <div className="auth-hero-brand">
            {logoDataUrl ? <img src={logoDataUrl} alt={appName} className="brand-logo" /> : <span className="brand-dot" />}
            <span>{appName}</span>
          </div>
          <h1 className="auth-hero-title">IA que entende o seu código e o seu contexto.</h1>
          <p className="auth-hero-sub">
            Respostas com base nos seus repositórios, documentação automática e modos de negócio,
            técnico e suporte - tudo com a identidade da sua empresa.
          </p>
          <ul className="auth-hero-list">
            <li>Leitura read-only dos seus repositórios (GitHub, GitLab, Azure DevOps)</li>
            <li>Documentação gerada e mantida automaticamente</li>
            <li>Multi-tenant e white-label por organização</li>
          </ul>
        </div>
      </aside>
      <div className="auth-pane">
      <div className="auth-card">
        <div className="auth-brand">
          {logoDataUrl ? (
            <img src={logoDataUrl} alt={appName} className="brand-logo" />
          ) : (
            <span className="brand-dot" />
          )}
          <span>{appName}</span>
        </div>
        <p className="auth-sub">Acesso restrito - entre ou crie sua conta.</p>

        {expirou && (
          <div className="auth-expired">
            Sua sessão expirou por inatividade. Entre de novo para voltar de onde parou.
          </div>
        )}

        {tab === "twofactor" ? (
          <>
            <p className="auth-sub" style={{ textAlign: "center" }}>
              {useBackupCode
                ? "Informe um dos seus códigos de backup."
                : "Abra o app autenticador e informe o código de 6 dígitos."}
            </p>
            <form className="auth-form" onSubmit={submit}>
              <label className="auth-field">
                <span>{useBackupCode ? "Código de backup" : "Código de verificação"}</span>
                <input
                  type="text"
                  value={twoFactorCode}
                  onChange={(e) => setTwoFactorCode(e.target.value)}
                  placeholder={useBackupCode ? "XXXXX-XXXXX" : "000000"}
                  autoComplete="one-time-code"
                  autoFocus
                  required
                />
              </label>
              {error && <div className="auth-error">{error}</div>}
              <button type="submit" className="auth-submit" disabled={busy}>
                {busy ? "Verificando…" : "Confirmar"}
              </button>
            </form>
            <p style={{ textAlign: "center", marginTop: 10 }}>
              <button
                type="button"
                style={{ background: "none", border: 0, cursor: "pointer", font: "inherit", fontSize: 12, color: "inherit", textDecoration: "underline" }}
                onClick={() => {
                  setUseBackupCode((v) => !v);
                  setTwoFactorCode("");
                  setError(null);
                }}
              >
                {useBackupCode ? "Usar código do app autenticador" : "Perdi o acesso ao app - usar código de backup"}
              </button>
            </p>
          </>
        ) : (
          <>
        <div className="auth-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === "login"}
            className={`auth-tab ${tab === "login" ? "active" : ""}`}
            onClick={() => {
              setTab("login");
              setError(null);
            }}
          >
            Entrar
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "register"}
            className={`auth-tab ${tab === "register" ? "active" : ""}`}
            onClick={() => {
              setTab("register");
              setError(null);
              setPendingMsg(null);
            }}
          >
            Criar conta
          </button>
        </div>

        {tab === "forgot" && (
          <p className="auth-sub" style={{ textAlign: "center", marginTop: 8 }}>
            <button
              type="button"
              style={{ background: "none", border: 0, cursor: "pointer", font: "inherit", color: "inherit", textDecoration: "underline" }}
              onClick={() => {
                setTab("login");
                setError(null);
              }}
            >
              Voltar para o login
            </button>
          </p>
        )}

        <form className="auth-form" onSubmit={submit}>
          {tab === "register" && (
            <label className="auth-field">
              <span>Nome</span>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Como quer ser chamado"
                autoComplete="name"
              />
            </label>
          )}
          <label className="auth-field">
            <span>E-mail</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="voce@empresa.com"
              autoComplete="email"
              required
            />
          </label>
          {tab !== "forgot" && (
          <label className="auth-field">
            <span>Senha</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={tab === "register" ? "Mínimo 8 caracteres" : "Sua senha"}
              autoComplete={tab === "register" ? "new-password" : "current-password"}
              required
              minLength={8}
            />
          </label>
          )}

          {tab === "login" && (
            <p style={{ textAlign: "right", marginTop: -4 }}>
              <button
                type="button"
                style={{ background: "none", border: 0, cursor: "pointer", font: "inherit", fontSize: 12, color: "inherit", textDecoration: "underline" }}
                onClick={() => {
                  setTab("forgot");
                  setError(null);
                  setPendingMsg(null);
                }}
              >
                Esqueci minha senha
              </button>
            </p>
          )}

          {tab === "forgot" && (
            <p className="auth-sub">
              O link de redefinição vai para o canal cadastrado desta pessoa no Teams, e vale por
              1 hora.
            </p>
          )}


          {error && <div className="auth-error">{error}</div>}
          {pendingMsg && <div className="auth-pending">{pendingMsg}</div>}

          <button type="submit" className="auth-submit" disabled={busy}>
            {busy
              ? "Aguarde…"
              : tab === "login"
                ? "Entrar"
                : tab === "forgot"
                  ? "Enviar link de redefinição"
                  : "Criar conta"}
          </button>
        </form>

        {tab !== "forgot" && ssoConfigured.length > 0 && (
          <div className="auth-sso">
            <p className="auth-sso-divider"><span>ou</span></p>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {ssoConfigured.map((p) => (
                <a key={p} className="auth-sso-btn" href={`/api/auth/sso/${p}/start`}>
                  Entrar com {SSO_LABEL[p]}
                </a>
              ))}
            </div>
          </div>
        )}

        {tab === "login" && (
          <div style={{ textAlign: "center", marginTop: 10 }}>
            <button
              type="button"
              style={{ background: "none", border: 0, cursor: "pointer", font: "inherit", fontSize: 12, color: "inherit", textDecoration: "underline" }}
              onClick={() => setShowSaml((v) => !v)}
            >
              Entrar com SSO corporativo (SAML)
            </button>
            {showSaml && (
              <form
                style={{ display: "flex", gap: 6, marginTop: 8 }}
                onSubmit={(e) => {
                  e.preventDefault();
                  if (samlOrgSlug.trim()) window.location.href = `/api/saml/login/${encodeURIComponent(samlOrgSlug.trim())}`;
                }}
              >
                <input
                  type="text"
                  value={samlOrgSlug}
                  onChange={(e) => setSamlOrgSlug(e.target.value)}
                  placeholder="identificador da sua organização"
                  style={{ flex: 1, padding: "6px 8px", fontSize: 13 }}
                />
                <button type="submit" className="auth-submit" style={{ width: "auto", padding: "6px 14px" }}>
                  Ir
                </button>
              </form>
            )}
          </div>
        )}

        <p className="auth-foot">
          {tab === "login"
            ? "Novas contas entram na organização deste endereço e passam por aprovação do administrador."
            : "Após criar, aguarde a liberação do administrador desta organização para entrar."}
        </p>
          </>
        )}
      </div>
      </div>
    </div>
  );
}
